import React, { useRef, useEffect } from 'react';
import clsx from 'clsx';
import gsap from 'gsap';
import { alpha, Box, Divider, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { usePacuV2State } from '@/components/pages/kiosk/PACUv2/PacuV2State';
import PACUv2BedIndicator from '@/components/pages/kiosk/PACUv2/PACUv2BedIndicator';
import { getColor } from '@/components/Panel/PanelContainer';
import { useScope } from '@/hooks/useScope';

interface ORItemProps {
  orName: string;
  patientId: any;
  patientName: string;
  patientPacuBed: string;
  surgeonName: string;
  status: string;
  disabled?: boolean;
}

const statusLabels = {
  InOr: 'IN OR',
  Ready: 'READY',
  AnestheticStart: 'ANESTHETIC START',
  ReadyForSurgery: 'READY FOR SURGERY',
  PhysicianIn: 'PHYSICIAN IN',
  TimeOut: 'TIMEOUT',
  AntibioticTime: 'ANTIBIOTIC TIME',
  TimeOut2: 'TIMEOUT 2',
  Ongoing: 'ONGOING',
  CallNextPatient: 'CALL NEXT PATIENT',
  Closing: 'CLOSING',
  SurgeonLeftOR: 'SURGEON LEFT OR',
  DressingOn: 'DRESSING ON',
  ProcedureEnd: 'PROCEDURE END',
  AnestheticEnd: 'ANESTHETIC END',
  Completed: 'COMPLETED',
};

const ORItem = ({ orName, patientId, patientName, patientPacuBed, surgeonName, status, disabled }: ORItemProps) => {
  const scope = useScope();
  // @ts-ignore
  const hospitalId = scope?.hospital?.id;
  const statusColor = getColor(status, hospitalId);
  const classes = useStyles({ statusColor, disabled });

  const [state, setState] = usePacuV2State();
  const assign = state?.type === 'AssignBed';

  // Disabled items cannot be selected or assign beds
  const selectable = Boolean(patientId) && state?.type === 'Idle' && !disabled;
  const selected = Boolean(patientId) && state?.type === 'AssignBed' && patientId === state.patientId && !disabled;

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rootRef.current) {
      gsap.to(rootRef.current, {
        scale: selected ? 1.04 : 1,
        opacity: assign ? (selected ? 1 : 0.25) : 1,
        duration: 0.25,
        ease: 'power2.out',
      });
    }
  }, [assign, selected]);

  return (
    <Paper
      ref={rootRef}
      variant="outlined"
      className={clsx(classes.root, {
        [classes.empty]: !status,
        [classes.selectable]: selectable,
        [classes.selected]: selected,
        [classes.disabled]: disabled,
      })}
      onClick={selectable ? () => setState({ type: 'AssignBed', patientId, patientPacuBed }) : undefined}
    >
      <Box className={classes.main}>
        <Box className={classes.primarySection}>
          <Typography variant="h5">{orName}</Typography>
          <Typography variant="h5">{statusLabels[status] ?? 'VACANT'}</Typography>
        </Box>
        <Box className={classes.secondarySection}>
          <Typography className={classes.secondaryText}>{surgeonName ?? '-'}</Typography>
          <Typography className={classes.secondaryText}>{patientName ?? '-'}</Typography>
        </Box>
      </Box>

      <Divider orientation="vertical" />

      <PACUv2BedIndicator status={status} bedNumber={patientPacuBed} backgroundColor={statusColor} />
    </Paper>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    borderColor: (props: { statusColor?: string; disabled?: boolean }) => props.statusColor || theme.palette.divider,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },
  empty: {
    backgroundColor: 'transparent',
    '& > *': {
      opacity: 0.4,
    },
  },
  disabled: {
    backgroundColor: 'transparent',
    '& > *': {
      opacity: 0.4,
    },
    cursor: 'not-allowed',
  },
  selectable: {
    cursor: 'pointer',
  },
  selected: {
    boxShadow: (props: { statusColor?: string; disabled?: boolean }) =>
      `${alpha(props.statusColor || theme.palette.primary.main, 0.75)} 0 0 1em`,
    borderColor: theme.palette.primary.main,
  },
  primarySection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  secondarySection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  secondaryText: {
    color: theme.palette.text.secondary,
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

export default ORItem;
